import { createUser } from '@packages/firebase/client';
import { Alert, Button, Input } from '@packages/ui/core';
import { Muted, Paragraph } from '@packages/ui/typography';
import { Link, useSubmit } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useMemo, useState } from 'react';
import { ValidatedForm, useField, useIsSubmitting, useIsValid } from 'remix-validated-form';
import { z } from 'zod';
import { FirebaseManager } from '~/firebase-manager';
import { getFirebaseAuthErrorMessage } from '~/shared/utils/get-firebase-auth-error-message';
import { MIN_PASSWORD_LENGTH, MIN_PASSWORD_SCORE, createPasswordStrengthRefinement, evaluatePassword } from '~/utils/password-strength';
import { PasswordStrengthInput } from './password-strength-input';

const BaseSignUpSchema = z.object({
  firstname: z
    .string()
    .trim()
    .min(1, { message: 'First name is required.' })
    .refine(value => value.trim().length > 0, { message: 'First name is required' }),
  lastname: z
    .string()
    .trim()
    .min(1, { message: 'Last name is required.' })
    .refine(value => value.trim().length > 0, { message: 'Last name is required' }),
  email: z.string().trim().toLowerCase().min(1, { message: 'Email is required.' }).email('Enter a valid email.'),
  password: z.string().min(MIN_PASSWORD_LENGTH, { message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` }),
});

const EnhancedSignUpSchema = BaseSignUpSchema.superRefine(
  createPasswordStrengthRefinement<z.infer<typeof BaseSignUpSchema>>({
    userInputsResolver: data => [data.email, data.firstname, data.lastname],
  }),
);

export const SignUpFormValidator = withZod(EnhancedSignUpSchema);

type SignUpFormProps = {
  email?: string;
  error?: string;
  onSubmit: (data: SignUpFormData) => void;
};

type SignUpFormData = {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
};

export function useSignUpFormSubmit() {
  const submit = useSubmit();

  const [error, setError] = useState<string>();

  return {
    onSubmit: async (data: SignUpFormData, action = '/sign-up') => {
      const client = FirebaseManager.getApp(data.email);

      try {
        const { user } = await createUserWithEmailAndPassword(client.auth, data.email, data.password);

        if (!user.email) {
          throw new Error('User email is missing.');
        }

        const userInputs = [data.email, data.firstname, data.lastname]
          .map(value => value?.trim().toLowerCase())
          .filter((value): value is string => Boolean(value));

        const result = evaluatePassword(data.password, userInputs);
        const hasSecurePassword = result.score >= MIN_PASSWORD_SCORE;

        await createUser(client.firestore, user.uid, {
          email: user.email,
          firstname: data.firstname,
          lastname: data.lastname,
          acceptedTermsVersion: 1,
          hasSecurePassword,
        });

        const token = await user.getIdToken();

        submit({ token }, { method: 'POST', action });
      } catch (err: any) {
        setError(getFirebaseAuthErrorMessage(err.code ?? 'auth/invalid-email'));
      }
    },
    error,
  };
}

export const SignUpForm = ({ email: _email, error, onSubmit }: SignUpFormProps) => {
  const firstname = useField('firstname', { formId: 'sign-up' });
  const lastname = useField('lastname', { formId: 'sign-up' });
  const email = useField('email', { formId: 'sign-up' });
  const [firstnameValue, setFirstnameValue] = useState<string>();
  const [lastnameValue, setLastnameValue] = useState<string>();
  const [emailValue, setEmailValue] = useState<string>();

  const userInputs = useMemo(() => {
    return [emailValue ?? _email, firstnameValue, lastnameValue]
      .map(value => value?.trim().toLowerCase())
      .filter((value): value is string => Boolean(value));
  }, [emailValue, _email, firstnameValue, lastnameValue]);

  const isValid = useIsValid('sign-up');
  const isSubmitting = useIsSubmitting('sign-up');

  const disabled = isSubmitting || !isValid;
  const isLoading = isSubmitting;

  return (
    <ValidatedForm
      id="sign-up"
      validator={SignUpFormValidator}
      onSubmit={(data, event) => {
        event.preventDefault();
        onSubmit(data);
      }}
      method="POST"
    >
      <div className="space-y-6">
        <div className="space-y-4">
          {error && (
            <Alert.Root color="danger">
              <Alert.Title>Something went wrong!</Alert.Title>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Root>
          )}

          <div className="flex space-x-4">
            <Input.Root
              {...firstname.getInputProps()}
              type="text"
              placeholder="John"
              onChange={event => setFirstnameValue(event.target.value)}
            >
              <Input.Header>
                <Input.Label>First name</Input.Label>
              </Input.Header>

              <Input.Field />

              {firstname.error && <Input.Error>{firstname.error}</Input.Error>}
            </Input.Root>

            <Input.Root
              {...lastname.getInputProps()}
              type="text"
              placeholder="Doe"
              onChange={event => setLastnameValue(event.target.value)}
            >
              <Input.Header>
                <Input.Label>Last name</Input.Label>
              </Input.Header>

              <Input.Field />

              {lastname.error && <Input.Error>{lastname.error}</Input.Error>}
            </Input.Root>
          </div>

          <Input.Root
            {...email.getInputProps()}
            type="email"
            placeholder="name@example.com"
            defaultValue={_email}
            onChange={event => setEmailValue(event.target.value)}
          >
            <Input.Header>
              <Input.Label>Email</Input.Label>
            </Input.Header>

            <Input.Field />

            {email.error && <Input.Error>{email.error}</Input.Error>}
          </Input.Root>

          <PasswordStrengthInput name="password" label="Password" formId="sign-up" userInputs={userInputs} />
        </div>

        <div>
          <Paragraph>
            <Muted className="text-sm">
              By creating an account you agree to our{' '}
              <Link to="/terms-of-use" target="_blank">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" target="_blank">
                Privacy Policy
              </Link>
              .
            </Muted>
          </Paragraph>
        </div>

        <Button type="submit" size="lg" disabled={disabled} isLoading={isLoading} isFullWidth>
          Sign Up
        </Button>
      </div>
    </ValidatedForm>
  );
};
